import { useEffect, useState, useLayoutEffect, useRef, useMemo } from 'react'
import {
  Text,
  ActivityIndicator,
  Platform,
  StyleSheet,
} from 'react-native'
import { useRouter } from 'expo-router'
import { useTheme } from '../../src/theme/useTheme'
import type { Theme } from '../../src/theme/getTheme'
import { JoinFlowScreen } from '../../src/join/components/JoinFlowScreen'
import * as Google from 'expo-auth-session/providers/google'

export default function OAuthGmailCallbackScreen() {
  const appTheme = useTheme()
  const styles = useMemo(() => createGmailCallbackStyles(appTheme), [appTheme])
  const router = useRouter()
  const hasRedirectedRef = useRef(false)
  const [shouldRedirect, setShouldRedirect] = useState(false)
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>(
    'processing'
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Use the same Google OAuth config as friends.tsx to process the redirect
  const webClientId = process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID
  const isWeb = Platform.OS === 'web'
  const redirectUri = isWeb
    ? `${window.location.origin}/oauth-gmail-callback`
    : undefined

  const googleConfig = {
    scopes: [
      'https://www.googleapis.com/auth/contacts.readonly',
      'https://www.googleapis.com/auth/userinfo.email',
      'https://www.googleapis.com/auth/gmail.send',
    ],
    additionalParameters: {
      prompt: 'consent',
    },
    webClientId:
      webClientId ||
      (isWeb ? 'placeholder-not-configured.apps.googleusercontent.com' : ''),
    redirectUri: isWeb ? redirectUri : undefined,
    useProxy: false,
  }

  // This hook will automatically detect and process the OAuth callback
  const [, response] = Google.useAuthRequest(googleConfig)

  // Check for token immediately in useLayoutEffect (runs synchronously before paint)
  // This prevents the component from rendering at all when we have a token
  useLayoutEffect(() => {
    if (
      Platform.OS !== 'web' ||
      hasRedirectedRef.current ||
      typeof window === 'undefined'
    ) {
      return
    }

    const hash = window.location.hash.substring(1)
    const hashParams = new URLSearchParams(hash)
    const accessToken = hashParams.get('access_token')

    if (accessToken) {
      hasRedirectedRef.current = true
      window.localStorage.setItem('gmail_oauth_token', accessToken)
      // Redirect immediately using client-side navigation
      router.replace('/app/friends')
      setShouldRedirect(true)
    }
  }, [router])

  // Note: Tab bar is automatically hidden by layout via HIDE_TAB_BAR_ROUTES
  // No manual override needed - the layout handles it properly

  // Handle errors and code flow (token in hash is handled by useLayoutEffect above)
  useEffect(() => {
    if (Platform.OS !== 'web' || shouldRedirect || hasRedirectedRef.current) {
      return
    }

    const returnUrl = '/app/friends'

    // Check for errors in hash or query string
    const hash = window.location.hash.substring(1)
    const hashParams = new URLSearchParams(hash)
    const error = hashParams.get('error')
    const errorDescription = hashParams.get('error_description')

    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    const queryError = urlParams.get('error')

    // Handle OAuth errors
    if (error || queryError) {
      const finalError = error || queryError
      setStatus('error')
      setErrorMessage(
        `OAuth error: ${finalError}${
          errorDescription ? ` - ${errorDescription}` : ''
        }`
      )
      setTimeout(() => {
        router.replace(returnUrl as string)
      }, 1500)
      return
    }

    // If we have a code, expo-auth-session will handle it (processed in response effect below)
    if (code) {
      return
    }

    // No token, code, or error found - wait for expo-auth-session to process
  }, [router, shouldRedirect])

  // Handle the OAuth response from expo-auth-session (for authorization code flow)
  useEffect(() => {
    if (!response || shouldRedirect || hasRedirectedRef.current) {
      return
    }

    const returnUrl = '/app/friends'

    if (response.type === 'success' && 'authentication' in response) {
      const token = response.authentication?.accessToken
      if (token) {
        hasRedirectedRef.current = true
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('gmail_oauth_token', token)
        }
        setShouldRedirect(true)
        router.replace(returnUrl as string)
      } else {
        setStatus('error')
        setErrorMessage('No access token received')
        setTimeout(() => {
          router.replace(returnUrl as string)
        }, 1500)
      }
    } else if (response.type === 'error' && 'errorCode' in response) {
      setStatus('error')
      setErrorMessage(`OAuth error: ${response.errorCode}`)
      setTimeout(() => {
        router.replace(returnUrl as string)
      }, 1500)
    }
  }, [response, router, shouldRedirect])

  // Inject CSS on web to hide navigation (only if we're actually rendering the screen)
  useEffect(() => {
    if (
      Platform.OS === 'web' &&
      !shouldRedirect &&
      !hasRedirectedRef.current &&
      typeof window !== 'undefined'
    ) {
      // Set page title
      document.title = 'Connecting to Gmail...'

      const style = document.createElement('style')
      style.id = 'oauth-gmail-callback-style'
      style.textContent = `
        body { overflow: hidden !important; margin: 0 !important; padding: 0 !important; }
        #root, [data-reactroot], [id^="expo"] { margin: 0 !important; padding: 0 !important; }
        nav[role="tablist"] { display: none !important; }
        [role="tablist"] { display: none !important; }
        header { display: none !important; }
        [class*="header"] { display: none !important; }
        /* Hide any route labels, breadcrumbs, or path indicators */
        [class*="route"], [class*="path"], [class*="breadcrumb"] { display: none !important; }
        /* Hide any text that might show the route name */
        *:not(script):not(style) {
          text-indent: 0 !important;
        }
        /* Specifically target any Expo Router route indicators */
        [data-testid*="route"], [aria-label*="route"], [title*="oauth"] { display: none !important; }
      `
      document.head.appendChild(style)
      return () => {
        // Clean up style when component unmounts
        const styleElement = document.getElementById(
          'oauth-gmail-callback-style'
        )
        if (styleElement && document.head.contains(styleElement)) {
          document.head.removeChild(styleElement)
        }
        // Restore default title
        document.title = 'Zporti'
      }
    }
  }, [shouldRedirect])

  // Don't render if we're redirecting (prevents any flash/splash)
  if (shouldRedirect || hasRedirectedRef.current) {
    return null
  }

  return (
    <JoinFlowScreen variant="loading">
      {status === 'processing' && (
        <>
          <ActivityIndicator size="large" color={appTheme.primary} />
          <Text style={styles.text}>Connecting to Gmail…</Text>
        </>
      )}
      {status === 'success' && (
        <>
          <Text style={styles.text}>Gmail connected successfully</Text>
          <Text style={styles.subtext}>Redirecting…</Text>
        </>
      )}
      {status === 'error' && (
        <>
          <Text style={styles.text}>Connection failed</Text>
          <Text style={styles.subtext}>{errorMessage}</Text>
          <Text style={styles.subtext}>Redirecting back…</Text>
        </>
      )}
    </JoinFlowScreen>
  )
}

function createGmailCallbackStyles(t: Theme) {
  return StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    text: {
      color: t.gray[900],
      fontSize: 18,
      fontWeight: '600',
      marginTop: 20,
      textAlign: 'center',
      paddingHorizontal: 24,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    subtext: {
      color: t.gray[600],
      fontSize: 14,
      marginTop: 10,
      textAlign: 'center',
      paddingHorizontal: 24,
    },
  })
}
